import {
	differenceInDays,
	endOfDay,
	format,
	isSameDay,
	startOfDay,
} from "date-fns";
import { enGB, pl } from "date-fns/locale";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { type DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { useTranslation } from "react-i18next";
import { BaseDatePicker } from "src/components/Default/Calendar/BaseDatePicker";
import { IconButton } from "src/components/Default/LekUI/Button";
import { isLekEn } from "src/helpers/services";
import ArrowLeftIcon from "src/images/Icons/newIcons/arrowLeft.svg";
import ArrowRightIcon from "src/images/Icons/newIcons/arrowRight.svg";

interface ChevronProps {
	orientation?: "left" | "right";
}

const Chevron = ({ orientation }: ChevronProps) => {
	const Icon = orientation === "left" ? ArrowLeftIcon : ArrowRightIcon;

	return (
		<IconButton>
			<Icon className="icon-small" />
		</IconButton>
	);
};

interface DateRangePickerProps {
	setDates: (dates: [Date, Date]) => void;
	dates: [Date, Date];
	renderTrigger: (props: any) => ReactNode;
	MAX_DAYS?: number;
	minDate?: Date;
	/** Pozwala wybrać dni po dzisiejszym (domyślnie zablokowane). */
	allowFuture?: boolean;
}

export const DateRangePicker = ({
	setDates,
	dates,
	renderTrigger: propsRenderTrigger,
	MAX_DAYS,
	minDate,
	allowFuture,
}: DateRangePickerProps) => {
	const { t } = useTranslation();
	const initialState: DateRange = { from: dates[0], to: dates[1] };
	const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
		initialState,
	);
	const [hoveredDate, setHoveredDate] = useState<Date | undefined>(undefined);
	const isDateSelected = selectedRange?.from && selectedRange.to;

	const rangeToHover = () => {
		if (selectedRange?.from && !selectedRange?.to && hoveredDate) {
			const [start, end] = [selectedRange.from, hoveredDate].sort(
				(a, b) => a.getTime() - b.getTime(),
			);
			if (MAX_DAYS !== undefined && differenceInDays(end, start) > MAX_DAYS) {
				return undefined;
			}
			return { from: start, to: end };
		}
		return undefined;
	};

	const range = rangeToHover();

	const isSingleDayRange = (
		day: Date,
		selectedRange: DateRange | undefined,
	) => {
		if (selectedRange?.from && selectedRange.to) {
			return (
				isSameDay(selectedRange.from, selectedRange.to) &&
				isSameDay(day, selectedRange.from)
			);
		}
		return false;
	};

	const hoverModifiers = useMemo(
		() => ({
			range_hover: range,
			hover_start: range?.from,
			hover_end: range?.to,
			singleDayRange: (day: Date) => isSingleDayRange(day, selectedRange),
		}),
		[selectedRange],
	);

	const hoverClassNames = {
		range_hover: "rdp-day_range_hover",
		hover_start: "rdp-day_hover_start",
		hover_end: "rdp-day_hover_end",
		singleDayRange: "rdp-single-day-circle",
	};

	const handleSelect = (range: DateRange | undefined) => {
		if (!range?.from && selectedRange?.from) {
			setSelectedRange({
				from: startOfDay(selectedRange.from),
				to: endOfDay(selectedRange.from),
			});
		} else {
			setSelectedRange(range);
		}
		setHoveredDate(undefined);
	};

	const handleDayMouseEnter = (date: Date) => {
		if (selectedRange?.from && !selectedRange.to) {
			setHoveredDate(date);
		}
	};

	const handleDayMouseLeave = () => {
		setHoveredDate(undefined);
	};

	const getCurrentRangeText = () => {
		if (isDateSelected) {
			return `${format(selectedRange.from!, "dd.MM")} - ${format(selectedRange.to!, "dd.MM")}`;
		}
		return t("datePicker.selectDate");
	};

	const onConfirm = (selected: DateRange) => {
		setDates([startOfDay(selected.from!), endOfDay(selected.to!)]);
	};

	const renderTrigger = useCallback(
		(props: any) => {
			return propsRenderTrigger({ ...props, isDateSelected, selectedRange });
		},
		[propsRenderTrigger, isDateSelected, selectedRange],
	);

	return (
		<BaseDatePicker
			titleKey={
				MAX_DAYS !== undefined
					? t("datePicker.selectRangeTitle", { maxDays: MAX_DAYS })
					: t("datePicker.selectRangeTitleNoMax", "Wybierz zakres dat")
			}
			buttonText={getCurrentRangeText()}
			isConfirmDisabled={!isDateSelected}
			initialState={initialState}
			selectedState={selectedRange}
			onConfirm={onConfirm}
			setState={setSelectedRange}
			renderTrigger={renderTrigger}
		>
			<DayPicker
				timeZone="Europe/Warsaw"
				animate
				firstWeekContainsDate={4}
				ISOWeek
				max={MAX_DAYS}
				min={1}
				mode="range"
				disabled={[
					...(allowFuture ? [] : [{ after: new Date() }]),
					...(minDate ? [{ before: minDate }] : []),
				]}
				defaultMonth={selectedRange?.to || new Date()}
				locale={isLekEn() ? enGB : pl}
				onDayMouseEnter={handleDayMouseEnter}
				onDayMouseLeave={handleDayMouseLeave}
				selected={selectedRange}
				onSelect={handleSelect}
				modifiers={hoverModifiers}
				modifiersClassNames={hoverClassNames}
				components={{
					Chevron,
				}}
			/>
		</BaseDatePicker>
	);
};
